import {
  MasterFieldDef,
  ResponseTypeEnum,
  ResponseURL,
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
  TemplatePageSchema,
} from '@ulysses-inc/harami_api_client'
import { DATETIME_RULE_MAX_VALUE } from 'src/constants/responseDatetime'
import { SECTION_NAME_MAX_LENGTH } from 'src/constants/section'
import { TemplatePagesState } from 'src/state/ducks/templates/templatePages/reducer'
import { ValidationResult, err, ok } from './types'
import {
  validateFormula,
  validateLabelCheck,
  validateQuestionAnswerOptions as validateQuestionAnswerOptionsOriginal,
  validateTimeMeasurement,
} from './validate'

const QUESTION_NAME_MAX_LENGTH = 255
const SCALE_MAX_LENGTH = 10

/**
 * 回答項目のバリデーション
 * 回答項目が未設定の質問がある場合はエラー
 */
export const validateResponseType = (
  templateNodes: TemplateNodeSchema[],
): ValidationResult => {
  for (const node of templateNodes) {
    if (node.type === TemplateNodeTypeEnum.Question) {
      // node.question.responseTypeは0が入り得る（型を信頼できない）ので対処
      if (
        node.question?.responseType == null ||
        (node.question.responseType as ResponseTypeEnum | 0) === 0
      ) {
        return err({
          type: 'page',
          message: `質問名：「${node.question?.name}」の回答項目が設定されていません`,
        })
      }
    }
  }
  return ok()
}

/**
 * インフォメーションノードのバリデーション
 * インフォメーション質問で必須設定かつ画像が未設定の場合はエラー
 */
export const validateInformationNode = (
  templateNodes: TemplateNodeSchema[],
): ValidationResult => {
  const informationNodeWithoutImage = templateNodes.find(
    (node: TemplateNodeSchema) =>
      node.question?.responseType === ResponseTypeEnum.INFORMATION &&
      node.question?.isRequired === 1 &&
      (node.question?.responseInformations ?? []).length === 0,
  )
  if (informationNodeWithoutImage) {
    return err({
      type: 'page',
      message: `質問名：「${informationNodeWithoutImage.question?.name}」の画像が設定されていません`,
    })
  }
  return ok()
}

/**
 * URL形式からプロトコルを抽出
 */
const getProtocol = (text: string): string | undefined => {
  try {
    return new URL(text).protocol
  } catch {
    return undefined
  }
}

/**
 * 単一のResponseURLのバリデーション
 */
const validateSingleResponseURL = (
  responseURL: ResponseURL,
  questionName: string,
): string | null => {
  if (responseURL.url === '') {
    return `質問名：「${questionName}」のURLを入力してください`
  }
  const protocol = getProtocol(responseURL.url)
  if (protocol === undefined) {
    return `質問名：「${questionName}」のURL形式が正しくありません`
  }
  if (!['https:', 'http:'].includes(protocol)) {
    return `質問名：「${questionName}」にはhttpsもしくはhttpから始まるURLを入力してください`
  }
  const responseURLMaxLength = 2000
  if (responseURL.url.length > responseURLMaxLength) {
    return `質問名：「${questionName}」のURLは${responseURLMaxLength}文字以内で入力してください`
  }
  return null
}

/**
 * ResponseURLのバリデーション
 * URL質問でURLが未設定または不正な形式の場合はエラー
 */
export const validateResponseURLs = (
  templateNodes: TemplateNodeSchema[],
): ValidationResult => {
  for (const node of templateNodes) {
    const isURLQuestion = node.question?.responseType === ResponseTypeEnum.URL
    if (!isURLQuestion) continue

    const responseURL = node.question?.responseURLs?.[0]
    if (!responseURL) continue

    const errMsg = validateSingleResponseURL(
      responseURL,
      node.question?.name ?? '',
    )
    if (errMsg !== null) {
      return err({ type: 'page', message: errMsg })
    }
  }
  return ok()
}

/**
 * 数式のバリデーション
 * 計算式質問で設定が不正な場合はエラー
 */
export const validateFormulaQuestions = (
  templatePagesData: TemplatePagesState,
): ValidationResult => {
  const templateNodes: TemplateNodeSchema[] = Object.values(
    templatePagesData.templateNodes,
  )
  const templatePages: TemplatePageSchema[] = Object.values(
    templatePagesData.templatePages,
  )
  const templateNodesDict = templatePagesData.templateNodes

  for (const node of templateNodes) {
    if (node.question?.responseType === ResponseTypeEnum.FORMULA) {
      const formulaMsg = validateFormula(node, {
        templatePages: templatePages,
        templateNodes: templateNodes,
        templateNodesDict: templateNodesDict,
      })
      if (formulaMsg) {
        return err({ type: 'page', message: formulaMsg })
      }
    }
  }
  return ok()
}

/**
 * ラベルチェックのバリデーション
 * ラベルチェック質問で設定が不正な場合はエラー
 */
export const validateLabelCheckQuestions = (
  templatePagesData: TemplatePagesState,
): ValidationResult => {
  const templateNodes: TemplateNodeSchema[] = Object.values(
    templatePagesData.templateNodes,
  )
  const templatePages: TemplatePageSchema[] = Object.values(
    templatePagesData.templatePages,
  )
  const templateNodesDict = templatePagesData.templateNodes

  for (const node of templateNodes) {
    if (node.question?.responseType === ResponseTypeEnum.LABEL_CHECK) {
      const labelCheckMsg = validateLabelCheck(node, {
        templatePages: templatePages,
        templateNodes: templateNodes,
        templateNodesDict: templateNodesDict,
      })
      if (labelCheckMsg) {
        return err({ type: 'page', message: labelCheckMsg })
      }
    }
  }
  return ok()
}

/**
 * 時間計測のバリデーション
 * 時間計測質問の計測設定(参照先の日時質問)が不正な場合はエラー
 */
export const validateTimeMeasurementQuestions = (
  templatePagesData: TemplatePagesState,
): ValidationResult => {
  const templateNodes: TemplateNodeSchema[] = Object.values(
    templatePagesData.templateNodes,
  )
  const templatePages: TemplatePageSchema[] = Object.values(
    templatePagesData.templatePages,
  )
  const templateNodesDict = templatePagesData.templateNodes

  for (const node of templateNodes) {
    if (node.question?.responseType !== ResponseTypeEnum.TIME_MEASUREMENT) {
      continue
    }
    const timeMeasurementMsg = validateTimeMeasurement(node, {
      templatePages,
      templateNodes,
      templateNodesDict,
    })
    if (timeMeasurementMsg) {
      return err({ type: 'page', message: timeMeasurementMsg })
    }
  }
  return ok()
}

/**
 * 質問回答オプションのバリデーション
 * 製品マスタから自動入力の設定が不正な場合はエラー
 */
export const validateQuestionAnswerOptions = (
  templateNodes: TemplateNodeSchema[],
  masterFieldDefs: MasterFieldDef[],
): ValidationResult => {
  const invalidAnswerOptionMsg = validateQuestionAnswerOptionsOriginal(
    templateNodes,
    masterFieldDefs,
  )

  if (invalidAnswerOptionMsg) {
    return err({ type: 'page', message: invalidAnswerOptionMsg })
  }
  return ok()
}

/**
 * 質問名の文字数バリデーション
 * 質問名が255文字を超える場合はエラー
 */
export const validateQuestionNameLength = (
  templateNodes: TemplateNodeSchema[],
): ValidationResult => {
  for (const node of templateNodes) {
    if (node.type !== TemplateNodeTypeEnum.Question) continue
    const name = node.question?.name ?? ''
    if ([...name].length > QUESTION_NAME_MAX_LENGTH) {
      return err({
        type: 'page',
        message: `質問名が${QUESTION_NAME_MAX_LENGTH}文字を超えている質問があります。${QUESTION_NAME_MAX_LENGTH}文字以内で入力してください`,
      })
    }
  }
  return ok()
}

/**
 * 数値質問・計算式質問の単位（scale）の文字数バリデーション
 * 単位が10文字を超える場合はエラー
 */
export const validateQuestionScale = (
  templateNodes: TemplateNodeSchema[],
): ValidationResult => {
  for (const node of templateNodes) {
    if (node.type !== TemplateNodeTypeEnum.Question) continue
    if (
      node.question?.responseType !== ResponseTypeEnum.NUMBER &&
      node.question?.responseType !== ResponseTypeEnum.FORMULA
    )
      continue
    const scale = node.question?.scale ?? ''
    if ([...scale].length > SCALE_MAX_LENGTH) {
      return err({
        type: 'page',
        message: `質問名：「${node.question?.name}」の単位は${SCALE_MAX_LENGTH}文字以内で入力してください`,
      })
    }
  }
  return ok()
}

/**
 * 日時質問ルールの日数バリデーション
 * ルールの日数が99999を超える場合はエラー
 */
export const validateDatetimeRuleValue = (
  templateNodes: TemplateNodeSchema[],
): ValidationResult => {
  for (const node of templateNodes) {
    if (node.type !== TemplateNodeTypeEnum.Question) continue
    if (node.question?.responseType !== ResponseTypeEnum.DATETIME) continue
    const rule = node.question?.responseDatetimes?.[0]?.rule
    if (rule === undefined) continue
    const absValue = Math.abs(rule.value ?? 0)
    if (absValue > DATETIME_RULE_MAX_VALUE) {
      return err({
        type: 'page',
        message: `質問名：「${node.question?.name}」の日時ルールの日数は${DATETIME_RULE_MAX_VALUE}以下で入力してください`,
      })
    }
  }
  return ok()
}

/**
 * セクション名の文字数バリデーション
 * セクション名が255文字を超える場合はエラー
 */
export const validateSectionNameLength = (
  templateNodes: TemplateNodeSchema[],
): ValidationResult => {
  for (const node of templateNodes) {
    if (node.type !== TemplateNodeTypeEnum.Section) continue
    const name = node.section?.name ?? ''
    if ([...name].length > SECTION_NAME_MAX_LENGTH) {
      return err({
        type: 'page',
        message: `セクション名が${SECTION_NAME_MAX_LENGTH}文字を超えているセクションがあります。${SECTION_NAME_MAX_LENGTH}文字以内で入力してください`,
      })
    }
  }
  return ok()
}
